import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable, map } from "rxjs";
import {
  Physician,
  PhysicianCreate,
  PhysicianLight
} from "src/app/backend/models/physician";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";

@Injectable()
export class PhysicianBackend {
  private readonly http: HttpClient = inject(HttpClient);

  @UserNotify({
    successMessage: marker("Medico esterno creato"),
    errorMessage: marker("Impossibile creare il medico esterno")
  })
  create(payload: PhysicianCreate): Observable<Physician> {
    return this.http.post<Physician>(
      `${environment.snoots.api.directory}/physicians`,
      payload
    );
  }

  load(id: string): Observable<Physician> {
    return this.http.get<Physician>(
      `${environment.snoots.api.directory}/physicians/${id}`
    );
  }

  loadLight(id: string): Observable<PhysicianLight> {
    return this.http.get<Physician>(
      `${environment.snoots.api.directory}/physicians/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  list(request: PageRequest): Observable<PaginatedResult<Physician>> {
    return this.http.get<PaginatedResult<Physician>>(
      `${environment.snoots.api.directory}/physicians`,
      {
        params: {
          p: request.page,
          s: request.pageSize,
          ...request.filterValues
        }
      }
    );
  }

  listLight(request: PageRequest): Observable<PaginatedResult<PhysicianLight>> {
    return this.http.get<PaginatedResult<PhysicianLight>>(
      `${environment.snoots.api.directory}/physicians`,
      {
        params: {
          p: request.page,
          s: request.pageSize,
          ...request.filterValues,
          light: true
        }
      }
    );
  }

  listAll(): Observable<Physician[]> {
    return this.list({
      page: 1,
      pageSize: 100000
    }).pipe(map((response) => response.docs));
  }

  listAllLight(): Observable<PhysicianLight[]> {
    return this.listLight({
      page: 1,
      pageSize: 100000
    }).pipe(map((response) => response.docs));
  }

  @UserNotify({
    successMessage: marker("Medico esterno aggiornato"),
    errorMessage: marker("Impossibile aggiornare il medico esterno")
  })
  update(id: string, payload: PhysicianCreate): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.directory}/physicians/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Drop page abilitata"),
    errorMessage: marker("Impossibile abilitare la drop page")
  })
  enableDropPage(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.directory}/physicians/${id}/enable-drop-page`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Drop page disabilitata"),
    errorMessage: marker("Impossibile disabilitare la drop page")
  })
  disableDropPAge(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.directory}/physicians/${id}/disable-drop-page`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Medico esterno rimosso"),
    errorMessage: marker("Impossibile rimuovere il medico esterno")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.directory}/physicians/${id}`
    );
  }
}
